import { useMemo } from 'react';
import { useFormikContext } from 'formik';
import {  Grid } from '@mui/material';
import { IFormFieldConfig } from '@shared/components/FormFieldsConstructor';
import { statesList } from '@shared/constants/statesList';
import { PhoneInputField } from '@shared/fields/PhoneInputField';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { ZipCodeInputField } from '@shared/fields/ZipCodeInputField';
import { LenderUtilityGridItem } from '../../LenderUtilityGridItem';
import { LenderUtilityFormPart } from '../../LenderUtilityFormPart';
import { TLenderUtilityGeneralForm } from './validation';

interface IGeneralFormProps {
  create?: boolean;
}

export type TLenderFormFieldConfig = IFormFieldConfig<TLenderUtilityGeneralForm>;

export const GeneralForm = ({ create }: IGeneralFormProps) => {
  const { values, setFieldValue } = useFormikContext<TLenderUtilityGeneralForm>();

  const isPayOffAddressForGAP = values.productCheckRequestPayee?.isPayOffAddressForGAP ?? false;
  const showGapPayoffAddress = isPayOffAddressForGAP;
  const handleCheckboxChange = (checked: boolean) => {
    setFieldValue('productCheckRequestPayee.isPayOffAddressForGAP', checked);
  };

  const lenderInformationForm: TLenderFormFieldConfig[] = [
    {
      width: '270px',
      name: 'name',
      showTooltip: true,
      maxLen: 31,
    },
    {
      width: `${!create} ? 135px : 0px`,
      name: 'code',
      hidden: !create,
      showTooltip: true,
      maxLen: 31,
    },
    {
      width: '231px',
      name: 'address',
    },
    {
      width: '231px',
      name: 'city',
    },
    {
      width: '104px',
      name: 'state',
      options: statesList,
      isFitContentWidth: true,
      component: SelectAutocompleteField,
    },
    {
      width: '120px',
      name: 'postCode',
      label: 'Zip',
      component: ZipCodeInputField,
    },
    {
      width: '231px',
      name: 'contactPhone',
      component: PhoneInputField,
    },
  ];

  const payeeCheckInformation: TLenderFormFieldConfig[] = [
    {
      width: '231px',
      name: 'productCheckRequestPayee.payeeName',
    },
    {
      width: '350px',
      name: 'productCheckRequestPayee.payeeAddress',
    },
    {
      width: '204px',
      name: 'productCheckRequestPayee.payeeState',
      options: statesList,
      component: SelectAutocompleteField,
    },
    {
      width: '120px',
      name: 'productCheckRequestPayee.payeeZip',
      label: 'Zip',
      component: ZipCodeInputField,
    },
  ];

  const gapLoanPaidOffRequestInformation: TLenderFormFieldConfig[] = [
    {
      width: '231px',
      name: 'productCheckRequestPayee.gapLoanPaidOffRequestInformation.payeeName',
    },
    {
      width: '350px',
      name: 'productCheckRequestPayee.gapLoanPaidOffRequestInformation.payeeAddress',
    },
    {
      width: '204px',
      name: 'productCheckRequestPayee.gapLoanPaidOffRequestInformation.payeeState',
      options: statesList,
      component: SelectAutocompleteField,
    },
    {
      width: '120px',
      name: 'productCheckRequestPayee.gapLoanPaidOffRequestInformation.payeeZip',
      label: 'Zip',
      component: ZipCodeInputField,
    },
  ];
  
  const formFieldsConfig = {
    lender: lenderInformationForm,
    payeeCheckInformation: payeeCheckInformation,
    gapLoanPaidOffRequestInformation: gapLoanPaidOffRequestInformation
  };

  return useMemo(
    () => (
      <Grid container sx={{ display: 'flex', flexDirection: 'column', height: '100%' }} columnSpacing={2}>
        <Grid container spacing={2} sx={{ flexGrow: 1, padding: 2 }}>
          <Grid item xs={6} sm={6} md={6} spacing={2}>
            <Grid container direction="column" rowGap={2}>
              <LenderUtilityGridItem direction='column' align='flex-start'>
                <LenderUtilityFormPart name="Lender Information" config={formFieldsConfig.lender} />
              </LenderUtilityGridItem>
            </Grid>
          </Grid>
          <Grid item xs={6} sm={6} md={6} spacing={2}>
            <Grid container direction="column" rowGap={2}>
              <LenderUtilityGridItem justify='flex-start'>
                <LenderUtilityFormPart
                  name="Lender Product Check Payee Information"
                  config={formFieldsConfig.payeeCheckInformation}
                  showCheckbox={true}
                  checkboxLabel="Different Payoff Address for GAP"
                  checkboxChecked={isPayOffAddressForGAP}
                  onCheckboxChange={handleCheckboxChange}
                />
              </LenderUtilityGridItem>
            </Grid>
          </Grid>
          <Grid item xs={6} sm={6} md={6} spacing={2}>
          </Grid>
          {showGapPayoffAddress && (
            <Grid item xs={6} sm={6} md={6} spacing={2}>
            <Grid container direction="column" rowGap={2}>
              <LenderUtilityGridItem justify='flex-start'>
                <LenderUtilityFormPart
                  name="GAP Loan Paid Off Request Information"
                  config={formFieldsConfig.gapLoanPaidOffRequestInformation}
                />
              </LenderUtilityGridItem>
            </Grid>
          </Grid>
          )}
        </Grid>
      </Grid>
    ), [formFieldsConfig]);
};
